import { StepState } from '@vmware-react/vrlcm';
import {
  DatacenterV2,
  EnvironmentState,
  PolicyProduct,
  RequestType
} from 'vrlcm-gui-types';
import * as Constants from '../../constants/action-constants';
import { StepBinaryMappingAction } from './steps/BinaryMapping';
import { StepEnvironmentAction } from './steps/Environment';
import { StepEulaAction } from './steps/Eula';
import { StepInfraAction } from './steps/Infrastructure';
import { StepNetworkAction } from './steps/Network';
import { StepPrecheckAction } from './steps/Precheck';
import { StepProductsAction } from './steps/Products';
import { StepSelectProductAction } from './steps/SelectProduct';

export interface GenerateEnvironmentRequest {
  type: Constants.GENERATE_ENVIRONMENT_REQUEST;
  payload: {
    isCloudProxyEnvironment: boolean;
  };
}

export interface GenerateEnvironmentRequestSuccess {
  type: Constants.GENERATE_ENVIRONMENT_REQUEST_SUCCESS;
  payload: {
    state: EnvironmentState;
    products: PolicyProduct[];
  };
}

export interface GenerateEnvironmentRequestFailed {
  type: Constants.GENERATE_ENVIRONMENT_REQUEST_FAILED;
}

export interface InitializeWorkflow {
  type: Constants.INITIALIZE_WORKFLOW;
  payload: {
    requestId?: string;
  };
}

export interface InitializeWorkflowSuccess {
  type: Constants.INITIALIZE_WORKFLOW_SUCCESS;
  payload: {
    datacenters: DatacenterV2[];
    state: EnvironmentState;
  };
}

export interface InitializeWorkflowFailed {
  type: Constants.INITIALIZE_WORKFLOW_FAILED;
}

export interface DestroyWorkflow {
  type: Constants.DESTROY_WORKFLOW;
}

export interface UpdateEnvironmentStepStates {
  type: Constants.UPDATE_ENVIRONMENT_STEP_STATES;
  payload: {
    stepStates: StepState[];
  };
}

export interface PatchEnvironmentState {
  type: Constants.PATCH_ENVIRONMENT_STATE;
  payload: {
    state: EnvironmentState;
  };
}

export interface PatchEnvironmentStateSuccess {
  type: Constants.PATCH_ENVIRONMENT_STATE_SUCCESS;
  payload: {
    state: EnvironmentState;
  };
}

export interface PatchEnvironmentStateFailed {
  type: Constants.PATCH_ENVIRONMENT_STATE_FAILED;
  payload: {
    message?: string;
  };
}

export interface SaveEnvironmentWorkflow {
  type: Constants.SAVE_ENVIRONMENT_WORKFLOW;
  payload: {
    state: EnvironmentState;
    exit?: boolean;
  };
}

export interface SaveEnvironmentWorkflowSuccess {
  type: Constants.SAVE_ENVIRONMENT_WORKFLOW_SUCCESS;
  payload: {
    state: EnvironmentState;
  };
}

export interface SaveEnvironmentWorkflowFailed {
  type: Constants.SAVE_ENVIRONMENT_WORKFLOW_FAILED;
  payload: {
    message?: string;
  };
}

export interface SubmitEnvironmentWorkflow {
  type: Constants.SUBMIT_ENVIRONMENT_WORKFLOW;
  payload: {
    state: EnvironmentState;
  };
}

export interface SubmitEnvironmentWorkflowSuccess {
  type: Constants.SUBMIT_ENVIRONMENT_WORKFLOW_SUCCESS;
}

export interface SubmitEnvironmentWorkflowFailed {
  type: Constants.SUBMIT_ENVIRONMENT_WORKFLOW_FAILED;
}

export interface UpdateRequestType {
  type: Constants.UPDATE_REQUEST_TYPE;
  payload: {
    requestType: RequestType;
  };
}

export type EnvironmentWorkflowAction =
  | GenerateEnvironmentRequest
  | GenerateEnvironmentRequestFailed
  | GenerateEnvironmentRequestSuccess
  | InitializeWorkflow
  | InitializeWorkflowFailed
  | InitializeWorkflowSuccess
  | DestroyWorkflow
  | UpdateEnvironmentStepStates
  | PatchEnvironmentState
  | PatchEnvironmentStateFailed
  | PatchEnvironmentStateSuccess
  | SaveEnvironmentWorkflow
  | SaveEnvironmentWorkflowFailed
  | SaveEnvironmentWorkflowSuccess
  | SubmitEnvironmentWorkflow
  | SubmitEnvironmentWorkflowSuccess
  | SubmitEnvironmentWorkflowFailed
  | UpdateRequestType
  | StepEnvironmentAction
  | StepSelectProductAction
  | StepEulaAction
  | StepInfraAction
  | StepNetworkAction
  | StepProductsAction
  | StepPrecheckAction
  | StepBinaryMappingAction;

export const generateEnvironmentRequest = (
  isCloudProxyEnvironment: boolean
): GenerateEnvironmentRequest => ({
  type: Constants.GENERATE_ENVIRONMENT_REQUEST,
  payload: { isCloudProxyEnvironment }
});

export const generateEnvironmentRequestSuccess = (
  state: EnvironmentState,
  products: PolicyProduct[]
): GenerateEnvironmentRequestSuccess => ({
  type: Constants.GENERATE_ENVIRONMENT_REQUEST_SUCCESS,
  payload: { state, products }
});

export const generateEnvironmentRequestFailed = (): GenerateEnvironmentRequestFailed => ({
  type: Constants.GENERATE_ENVIRONMENT_REQUEST_FAILED
});

export const initializeWorkflow = (requestId?: string): InitializeWorkflow => ({
  type: Constants.INITIALIZE_WORKFLOW,
  payload: { requestId }
});

export const initializeWorkflowSuccess = (
  datacenters: DatacenterV2[],
  state: EnvironmentState
): InitializeWorkflowSuccess => ({
  type: Constants.INITIALIZE_WORKFLOW_SUCCESS,
  payload: { datacenters, state }
});

export const initializeWorkflowFailed = (): InitializeWorkflowFailed => ({
  type: Constants.INITIALIZE_WORKFLOW_FAILED
});

export const destroyWorkflow = (): DestroyWorkflow => ({
  type: Constants.DESTROY_WORKFLOW
});

export const updateEnvironmentStepStates = (
  stepStates: StepState[]
): UpdateEnvironmentStepStates => ({
  type: Constants.UPDATE_ENVIRONMENT_STEP_STATES,
  payload: { stepStates }
});

export const patchEnvironmentState = (
  state: EnvironmentState
): PatchEnvironmentState => ({
  type: Constants.PATCH_ENVIRONMENT_STATE,
  payload: { state }
});

export const patchEnvironmentStateSuccess = (
  state: EnvironmentState
): PatchEnvironmentStateSuccess => ({
  type: Constants.PATCH_ENVIRONMENT_STATE_SUCCESS,
  payload: { state }
});

export const patchEnvironmentStateFailed = (
  message?: string
): PatchEnvironmentStateFailed => ({
  type: Constants.PATCH_ENVIRONMENT_STATE_FAILED,
  payload: { message }
});

export const saveEnvironmentWorkflow = (
  state: EnvironmentState,
  exit?: boolean
): SaveEnvironmentWorkflow => ({
  type: Constants.SAVE_ENVIRONMENT_WORKFLOW,
  payload: { state, exit }
});

export const saveEnvironmentWorkflowSuccess = (
  state: EnvironmentState
): SaveEnvironmentWorkflowSuccess => ({
  type: Constants.SAVE_ENVIRONMENT_WORKFLOW_SUCCESS,
  payload: { state }
});

export const saveEnvironmentWorkflowFailed = (
  message?: string
): SaveEnvironmentWorkflowFailed => ({
  type: Constants.SAVE_ENVIRONMENT_WORKFLOW_FAILED,
  payload: { message }
});

export const submitEnvironmentWorkflow = (
  state: EnvironmentState
): SubmitEnvironmentWorkflow => ({
  type: Constants.SUBMIT_ENVIRONMENT_WORKFLOW,
  payload: { state }
});

export const submitEnvironmentWorkflowSuccess = (): SubmitEnvironmentWorkflowSuccess => ({
  type: Constants.SUBMIT_ENVIRONMENT_WORKFLOW_SUCCESS
});

export const submitEnvironmentWorkflowFailed = (): SubmitEnvironmentWorkflowFailed => ({
  type: Constants.SUBMIT_ENVIRONMENT_WORKFLOW_FAILED
});

export const updateRequestType = (
  requestType: RequestType
): UpdateRequestType => ({
  type: Constants.UPDATE_REQUEST_TYPE,
  payload: { requestType }
});
